import React, { useEffect, useMemo, useRef } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../../utils/dataUtils';
import { useCurrency } from '../../hooks/useCurrency';
import { useTheme } from '../../hooks/useTheme';
import { getSeriesColors, getChartTheme, tooltipStyle, ChartEmptyState } from './chartTheme';

// A doughnut reads well for a handful of parts; the rest is folded into "Other".
const MAX_SEGMENTS = 5;
const OTHER_LABEL = 'Other';

// Splits category totals into doughnut segments (spend only) and the
// categories that reduce spend, which are not part of the whole.
export const buildCategorySegments = (data, themeName = 'light') => {
  const entries = Object.entries(data || {});
  const positive = entries.filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
  const reductions = entries.filter(([, value]) => value < 0).sort((a, b) => a[1] - b[1]);

  // Up to six categories are shown as they are; beyond that the smallest
  // are grouped so the chart never has more than six segments.
  const keep = positive.length <= MAX_SEGMENTS + 1 ? positive.length : MAX_SEGMENTS;
  const colors = getSeriesColors(positive.slice(0, keep).map(([name]) => name), themeName);
  const top = positive.slice(0, keep).map(([name, value], index) => ({
    name,
    value,
    color: colors[index],
    selectable: true
  }));
  const rest = positive.slice(keep);
  if (rest.length > 0) {
    top.push({
      name: `${OTHER_LABEL} (${rest.length})`,
      value: rest.reduce((sum, [, value]) => sum + value, 0),
      color: getChartTheme(themeName).neutral,
      selectable: false
    });
  }

  const total = top.reduce((sum, segment) => sum + segment.value, 0);
  return {
    segments: top.map((segment) => ({
      ...segment,
      share: total > 0 ? (segment.value / total) * 100 : 0
    })),
    reductions: reductions.map(([name, value]) => ({ name, value })),
    total
  };
};

export const CategoryChart = ({ data, onCategorySelect }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const currencyKey = useCurrency();
  const themeName = useTheme();

  const { segments, reductions, total } = useMemo(
    () => buildCategorySegments(data, themeName),
    [data, themeName]
  );

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Return early if there is nothing to draw
    if (segments.length === 0) {
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    const theme = getChartTheme(themeName);
    const labels = segments.map((segment) => segment.name);

    chartInstance.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: segments.map((segment) => segment.value),
          backgroundColor: segments.map((segment) => segment.color),
          // A gap in the surface colour separates the segments
          borderWidth: 2,
          borderColor: theme.ink.surface,
          hoverBorderColor: theme.ink.surface,
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        layout: { padding: 4 },
        plugins: {
          tooltip: {
            ...tooltipStyle(theme),
            callbacks: {
              label: context => {
                const segment = segments[context.dataIndex];
                return `${context.label}: ${formatCurrency(context.raw)} (${Math.round(segment.share)}%)`;
              }
            }
          },
          // The legend is rendered as a list next to the chart, with amounts
          legend: { display: false }
        }
      },
      plugins: [
        {
          id: 'customCanvasBackgroundColor',
          afterDatasetsDraw(chart) {
            chart.canvas.style.cursor = 'pointer';
          }
        }
      ]
    });

    // Add click event handler to the canvas
    const handleCanvasClick = (event) => {
      const points = chartInstance.current.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
      if (points.length > 0) {
        const segment = segments[points[0].index];
        if (segment && segment.selectable && onCategorySelect) {
          onCategorySelect(segment.name);
        }
      }
    };

    const canvas = chartRef.current;
    canvas.addEventListener('click', handleCanvasClick);

    return () => {
      if (canvas) {
        canvas.removeEventListener('click', handleCanvasClick);
      }
      if (chartInstance.current && typeof chartInstance.current.destroy === 'function') {
        chartInstance.current.destroy();
      }
    };
  }, [segments, onCategorySelect, currencyKey, themeName]);

  const hasData = segments.length > 0 || reductions.length > 0;

  if (!hasData) {
    return (
      <div style={{ height: '100%', position: 'relative' }}>
        <ChartEmptyState>No category data available</ChartEmptyState>
      </div>
    );
  }

  const renderRow = (name, value, { color, share, selectable }) => {
    const content = (
      <>
        <span className="legend-swatch" style={{ backgroundColor: color }} aria-hidden="true"></span>
        <span className="legend-name">{name}</span>
        <span className="legend-value">{formatCurrency(value)}</span>
        <span className="legend-share">{share === undefined ? '' : `${Math.round(share)}%`}</span>
      </>
    );
    return (
      <li key={name}>
        {selectable && onCategorySelect ? (
          <button
            type="button"
            className="legend-row"
            onClick={() => onCategorySelect(name)}
            aria-label={`${name}: ${formatCurrency(value)}. View line items`}
          >
            {content}
          </button>
        ) : (
          <div className="legend-row">{content}</div>
        )}
      </li>
    );
  };

  return (
    <div className="doughnut-layout">
      {segments.length > 0 && (
        <div className="doughnut-figure">
          <canvas ref={chartRef} role="img" aria-label="Doughnut chart of spend by category. The list beside it gives each amount." />
          <div className="doughnut-center" aria-hidden="true">
            <span className="doughnut-center-label">Spend</span>
            <span className="doughnut-center-value">{formatCurrency(total)}</span>
          </div>
        </div>
      )}

      <div className="chart-legend">
        <ul>
          {segments.map((segment) => renderRow(segment.name, segment.value, segment))}
        </ul>

        {reductions.length > 0 && (
          <>
            <p className="legend-heading">Not in the chart: reduces spend</p>
            <ul>
              {reductions.map((entry) =>
                renderRow(entry.name, entry.value, { color: 'transparent', selectable: true })
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
